import { useCallback, useEffect, useMemo, useState } from 'react';

import { observer } from 'mobx-react';
import moment from 'moment';
import { EndOfWeekDay } from '@bringg/types';
import { Button, Notification, Select, Space } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';

import Calendar from '../../components/calendar/calendar';
import EventDialog from './event-dialog';
import { EventFormData, normalizeEvents } from '../data-utils';
import UpdateDialog from './update-dialog';
import { useSpeedFactorTranslation } from '../translations';
import { useStores } from 'bringg-web/recipes';
import { timezoneProvider } from 'bringg-web/services/timezone/timezone-provider';
import { TimezoneContext } from '../context/timezone-context';
import { SpeedFactorGroup } from '../speed-factor-grouping/types';
import type { TimezoneOption } from '../types';

import styles from './traffic-calendar.module.scss';

export const allWeek = [
	EndOfWeekDay.Sunday,
	EndOfWeekDay.Monday,
	EndOfWeekDay.Tuesday,
	EndOfWeekDay.Wednesday,
	EndOfWeekDay.Friday,
	EndOfWeekDay.Saturday,
	EndOfWeekDay.Thursday
];

type Props = {
	serviceAreaId: number;
	additionalTimezones?: TimezoneOption[];
};

export const TrafficCalendar = observer(({ serviceAreaId, additionalTimezones = [] }: Props) => {
	const { serviceAreasStore } = useStores();
	const { t } = useTranslation();

	const [calendarEvents, setCalendarEvents] = useState([]);
	const [open, setOpen] = useState(false);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [modifiedEvent, setModifiedEvent] = useState(null);
	const [originalModifiedEvent, setOriginalModifiedEvent] = useState(null);
	const [originalModifiedEventIndex, setOriginalModifiedEventIndex] = useState(null);
	const [selectedTimezoneId, setSelectedTimezoneId] = useState(0);

	const timezoneOptions = useMemo<{ id: number; name: string; value: string }[]>(
		() => [
			{ name: t('PLANNED_DELIVERY_WINDOWS.BY_MERCHANT'), value: timezoneProvider.getTimezoneByMerchant(), id: 0 },
			...additionalTimezones.map(({ label, timezone }, index) => ({
				name: label,
				value: timezone,
				id: index + 1
			})),
			{ name: 'Etc/UTC', value: 'UTC', id: additionalTimezones.length + 1 }
		],
		[additionalTimezones]
	);

	const timezone = timezoneOptions[selectedTimezoneId].value;
	const translations = useSpeedFactorTranslation();

	const groupedSpeedFactors: SpeedFactorGroup[] = serviceAreasStore.getSpeedFactors(serviceAreaId);

	useEffect(() => {
		const fetchData = async () => {
			try {
				await serviceAreasStore.fetchAll();
				await serviceAreasStore.fetchSpeedFactors(serviceAreaId);
			} catch (err) {
				// eslint-disable-next-line no-console
				console.log(err);
				Notification.error('Failed to load speed factors');
			}
		};

		fetchData();
	}, [serviceAreasStore, serviceAreaId]);

	useEffect(() => {
		// There is a kind of logical issue with "UTC midnight split" speed factors.
		// When generating the calendar events, we consider both leading and trailing pieces as a single event.
		// This in turn may result in a situation when both leading and trailing pieces are shown / not shown
		// on the calendar while only one of them satisfies the constraints / must be shown.
		// When and if we render those correct, we should consider that some data is collected directly from the
		// calendar event - like the duration. In this case, properly trimmed calendar event belongs to the same
		// traffic variable config (defined by original_uuid) but represents a trimmed duration.
		// With current design, editing trimmed event will apply the "trimming" to other events belonging to the same
		// traffic variable config.
		if (groupedSpeedFactors) {
			setCalendarEvents(normalizeEvents(groupedSpeedFactors, 2, null, true, timezone)); // Generate events for 2 years plus previous year
		}
	}, [groupedSpeedFactors, timezone]);

	const handleOk = () => {
		setOpen(false);
		setSelectedEvent(null);
		setModifiedEvent(null);
		serviceAreasStore.fetchSpeedFactors(serviceAreaId);
	};

	const handleCancel = () => {
		setOpen(false);
		setSelectedEvent(null);
		setModifiedEvent(null);
	};

	const handleModifiedEventSubmit = () => {
		setModifiedEvent(null);
		setSelectedEvent(null);
		setOriginalModifiedEvent(null);
		setOriginalModifiedEventIndex(null);
		serviceAreasStore.fetchSpeedFactors(serviceAreaId);
	};

	const handleSelectEvent = useCallback(
		event => {
			setSelectedEvent(event);
			setOpen(true);
		},
		[selectedEvent]
	);

	const handleModifiedEventCancel = () => {
		setModifiedEvent(null);
		setSelectedEvent(null);

		if (originalModifiedEvent) {
			calendarEvents[originalModifiedEventIndex] = originalModifiedEvent;

			setCalendarEvents(calendarEvents);
			setOriginalModifiedEvent(null);
			setOriginalModifiedEventIndex(null);
		}
	};

	useEffect(() => {
		// Temporary event replacement in calendarEvents to avoid modified event jump while submitting next modal
		if (modifiedEvent && !originalModifiedEvent) {
			const originalModified = calendarEvents.find(item => item === modifiedEvent.event);
			const originalModifiedIndex = calendarEvents.indexOf(modifiedEvent.event);

			setOriginalModifiedEvent(originalModified);
			setOriginalModifiedEventIndex(originalModifiedIndex);

			// Mind that originalModified.originalEvent might be a speed factor group proxy object
			calendarEvents[originalModifiedIndex] = {
				...originalModified,
				start: modifiedEvent.start,
				end: modifiedEvent.end
			};

			setCalendarEvents(calendarEvents);
		}
	}, [calendarEvents, modifiedEvent, originalModifiedEvent]);

	const handleEventResizeOrDrop = event => {
		setModifiedEvent(event);
	};

	const getModifiedEventPayload = useCallback((eventData): EventFormData => {
		return {
			effectiveDates: [moment(eventData.event.originalStart), moment(eventData.event.originalEnd)],
			intervals: [moment(eventData.start), moment(eventData.end)],
			factor: eventData.event.factor,
			timeFrame: 'specificHours'
		};
	}, []);

	if (!groupedSpeedFactors) return <div data-test-id="traffic-calendar">{t('GLOBAL.LOADING')}...</div>;

	const utcHoursOffset = moment().tz(timezone).utcOffset() / 60;
	const utcOffsetLabel = `${utcHoursOffset >= 0 ? '+' : '-'}${Math.abs(utcHoursOffset)
		.toString()
		.padStart(2, '0')}:00`;

	return (
		<TimezoneContext.Provider value={timezone}>
			<div data-test-id="traffic-calendar">
				<Space direction="vertical" style={{ display: 'flex' }}>
					<Space style={{ display: 'flex', justifyContent: 'space-between' }}>
						<div className={styles.timezoneWrapper}>
							<Select
								showSearch
								className={styles.timezoneSelect}
								value={selectedTimezoneId}
								options={timezoneOptions}
								onChange={id => setSelectedTimezoneId(id)}
							/>
							{`${timezone} (UTC${utcOffsetLabel})`}
						</div>
						<Button type="primary" onClick={() => setOpen(true)}>
							{translations.addButton}
						</Button>
					</Space>
					<Calendar
						events={calendarEvents}
						views={['week']}
						toolbar={true}
						selected={selectedEvent}
						onSelectEvent={handleSelectEvent}
						onEventResize={handleEventResizeOrDrop}
						onEventDrop={handleEventResizeOrDrop}
						timezone={timezone}
					/>
				</Space>

				{open && (
					<EventDialog
						open={open}
						onSubmit={handleOk}
						onCancel={handleCancel}
						event={selectedEvent}
						serviceAreaId={serviceAreaId}
						groupedSpeedFactors={groupedSpeedFactors}
						timezone={timezone}
					/>
				)}

				{modifiedEvent && (
					<UpdateDialog
						open
						onOk={handleModifiedEventSubmit}
						onCancel={handleModifiedEventCancel}
						event={modifiedEvent.event}
						values={getModifiedEventPayload(modifiedEvent)}
						groupedSpeedFactors={groupedSpeedFactors}
						timezone={timezone}
					/>
				)}
			</div>
		</TimezoneContext.Provider>
	);
});

export default TrafficCalendar;
